import { type Location, Router, useLocation, useMatch, useNavigate } from "@solidjs/router";
import { FileRoutes } from "@solidjs/start/router";
import { createEffect, on, type ParentComponent, Show, Suspense } from "solid-js";
import "./app.css";
import { MinimalLayoutProvider } from "~/components/layouts/minimal";
import { I18nProvider } from "~/i18n/context";
import { ArtifactStoreProvider } from "~/stores/ArtifactStore";
import { useWire, WireProvider } from "~/wire";
import { ChatStreamPage } from "./page/chat/ChatStreamPage";
import { FeatureBaseMessengerInit } from "./third-party/featurebase";
import { stAnalytics } from "@storytell/analytics";

// App constructs the application and provides the main entry point for the
// application. It is responsible for initializing and rendering
// the application.
export default function App() {
  return (
    <Suspense>
      <Router
        root={(props) => {
          return (
            <>
              <I18nProvider>
                <WireProvider>
                  <PostAuthRedirect />
                  <ArtifactStoreProvider>
                    <MinimalLayoutProvider>
                      <InterceptChatPages>{props.children}</InterceptChatPages>
                    </MinimalLayoutProvider>
                    <FeatureBaseMessengerInit />
                  </ArtifactStoreProvider>
                </WireProvider>
              </I18nProvider>
              <PageViewTracker />
            </>
          );
        }}
      >
        <FileRoutes />
      </Router>
    </Suspense>
  );
}

export const PostAuthRedirect = () => {
  const wire = useWire();
  const navigate = useNavigate();

  createEffect(() => {
    const action = wire.auth.postAuthAction();
    if (wire.auth.isRegistered()) {
      if (action?.startsWith("refill:")) {
        const amount = Number.parseInt(action.split(":")[1]);
        navigate("/pricing", {
          state: {
            refill: amount,
          },
        });
        wire.auth.setPostAuthAction(undefined);
        return;
      }

      if (action?.startsWith("subscribe-pro:")) {
        const period = action.split(":")[1] as "monthly" | "yearly";
        navigate("/pricing", {
          state: {
            subscribe: period,
          },
        });
        wire.auth.setPostAuthAction(undefined);
        return;
      }
    }
  });

  return null;
};

/**
 * Needed so that the chat stream page doesn't get re-mounted when navigating between home and chats
 */
export const InterceptChatPages: ParentComponent = (props) => {
  const matchRoot = useMatch(() => "/");
  const matchChat = useMatch(() => "/chat/:chatId");
  return (
    <Show when={matchChat() || matchRoot()} fallback={props.children}>
      <ChatStreamPage />
    </Show>
  );
};

const PageViewTracker = () => {
  const location = useLocation();
  createEffect(
    on(
      () => [location.pathname, location.search, location.hash],
      () => {
        stAnalytics.page();

        // Let GTM know the page has changed
        // biome-ignore lint/suspicious/noExplicitAny: <explanation>
        (window as any).dataLayer?.push({
          event: "pageview",
          page: window.location.href,
        });

        const adgroup = parseUrlForAdgroup(location);
        if (adgroup) {
          stAnalytics.plugins.posthog.setPersonProperties({ adgroup: adgroup });
          stAnalytics.track("adgroup_campaign", { adgroup_event: adgroup });
        }
      },
    ),
  );
  return undefined;
};

export const parseUrlForMarketing = (
  location: Location<unknown>,
): { hasProperties: boolean; properties: Record<string, string | undefined> } => {
  const params = [
    "adgroup",
    "utm_source",
    "utm_medium",
    "utm_campaign",
    "utm_content",
    "utm_term",
    "network",
    "device",
    "placement",
  ];
  const properties: Record<string, string | undefined> = {};
  let hasProperties = false;

  for (const param of params) {
    if (location.query[param]) {
      properties[param] = location.query[param] as string;
      hasProperties = true;
    }
  }
  return {
    hasProperties,
    properties,
  };
};

/**
 * ENG-1500 - Parse URL for adgroup, used to setPersonProperties.
 */
export const parseUrlForAdgroup = (location: Location<unknown>) => {
  if (!location.query.adgroup) return;
  return location.query.adgroup as string;
};
